import { config } from '@/config/config';
import type { BibMarketConfirmInfo } from '@/types/bibmarket';

// Reclama (lado entrante, fase 1) un dorsal publicado en la bolsa de inscripciones. Endpoint
// anónimo: el BE valida (email != titular, no reclamado antes) y reserva atómicamente vía SP
// (el primero que reserve gana). En OK la publicación desaparece del listado. El email con la
// ruta del flujo entrante se enviará en un pase posterior (aún no cableado en BE).
export interface BibMarketClaimResult {
  ok: boolean;
  errorId?: string;
  status: number;
  // Solo en confirm(confirm=true): la reserva entrante creada por el BKD (alta del entrante).
  reservationId?: string;
  anonymousId?: string;
  // QuestionIds de recompra obligatoria: preseleccionados Y bloqueados (disabled) en el form.
  lockedQuestionIds?: number[];
}

export async function claimBibMarketOffer(
  lang: string,
  eventId: number,
  changeOwnerId: number,
  email: string,
): Promise<BibMarketClaimResult> {
  const path = `/api/site/registration/${encodeURIComponent(lang)}/${eventId}/my-registration/bibmarket/${changeOwnerId}/claim`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (config.api?.apiKey) headers['X-API-Key'] = config.api.apiKey;
  try {
    const res = await fetch(`${config.reservation.apiUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
      signal: AbortSignal.timeout(config.reservation.timeout),
    });
    if (res.ok) return { ok: true, status: res.status };
    const body = (await res.json().catch(() => ({}))) as { errorId?: string };
    return { ok: false, errorId: body.errorId, status: res.status };
  } catch {
    return { ok: false, status: 0 };
  }
}

// Lookup del proceso por la key del magic link (página de confirmación del entrante). Anónimo.
// Devuelve el recap del dorsal o null (404 = enlace inválido/caducado). Pensado para SSR.
export async function getBibMarketConfirm(
  lang: string,
  eventId: number,
  key: string,
): Promise<BibMarketConfirmInfo | null> {
  const path = `/api/site/registration/${encodeURIComponent(lang)}/${eventId}/my-registration/bibmarket/confirm/${encodeURIComponent(key)}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (config.api?.apiKey) headers['X-API-Key'] = config.api.apiKey;
  try {
    const res = await fetch(`${config.reservation.apiUrl}${path}`, { method: 'GET', headers, cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as { data?: BibMarketConfirmInfo };
    return body.data ?? null;
  } catch {
    return null;
  }
}

// Valida el email asociado (confirm=false) o confirma el inicio del proceso (confirm=true →
// SP usp_update_EventChangeOwnerEmailConfirmed). errorId `email_mismatch` si el email no
// coincide con el destino del proceso.
export async function confirmBibMarketEmail(
  lang: string,
  eventId: number,
  key: string,
  email: string,
  confirm: boolean,
): Promise<BibMarketClaimResult> {
  const path = `/api/site/registration/${encodeURIComponent(lang)}/${eventId}/my-registration/bibmarket/confirm/${encodeURIComponent(key)}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (config.api?.apiKey) headers['X-API-Key'] = config.api.apiKey;
  try {
    const res = await fetch(`${config.reservation.apiUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, confirm }),
      signal: AbortSignal.timeout(config.reservation.timeout),
    });
    if (res.ok) {
      const body = (await res.json().catch(() => ({}))) as { data?: { reservationId?: string; anonymousId?: string; lockedQuestionIds?: number[] } };
      return { ok: true, status: res.status, reservationId: body.data?.reservationId, anonymousId: body.data?.anonymousId, lockedQuestionIds: body.data?.lockedQuestionIds };
    }
    const errBody = (await res.json().catch(() => ({}))) as { errorId?: string };
    return { ok: false, errorId: errBody.errorId, status: res.status };
  } catch {
    return { ok: false, status: 0 };
  }
}
